/**
 * Ações otimistas com desfazer (SZ-1589).
 *
 * Contrato: `undoableAction` aplica a mudança na UI imediatamente (`apply`) e
 * ADIA o request ao servidor (`commit`) até o toast expirar (5s). Se o usuário
 * clicar em "Desfazer", `revert` roda e NADA foi enviado ao servidor — o commit
 * nunca acontece. O toast é renderizado pelo <UndoToastHost/> montado nos
 * layouts principais.
 *
 * O estado vive em módulo (fora do React) de propósito: navegações Inertia não
 * desmontam os timers, então um commit pendente dispara mesmo se o usuário
 * trocar de página antes dos 5s. Em unload real da página (`pagehide`) as
 * ações pendentes são canceladas localmente; não tentamos disparar requests
 * comuns durante o fechamento da aba porque o navegador pode abortá-los.
 */

export interface UndoableActionOptions {
  /** Texto do toast, ex.: 'Rotina "X" pausada'. */
  message: string
  /** Mudança otimista na UI; roda imediatamente. */
  apply: () => void
  /** Reverte a UI quando o usuário desfaz; nenhum request foi enviado. */
  revert: () => void
  /** Request real ao servidor; roda só quando o toast expira sem desfazer. */
  commit: () => void
  /** Rótulo do botão de desfazer (padrão: "Desfazer"). */
  undoLabel?: string
  /** Janela para desfazer, em ms (padrão: 5000). */
  duration?: number
}

export interface UndoToast {
  id: number
  message: string
  undoLabel: string
}

interface PendingEntry {
  toast: UndoToast
  timer: ReturnType<typeof setTimeout>
  revert: () => void
  commit: () => void
}

const UNDO_TOAST_DURATION_MS = 5000

const entries = new Map<number, PendingEntry>()
const listeners = new Set<() => void>()
let toastsSnapshot: UndoToast[] = []
let nextId = 1
let pageHideBound = false

function emit() {
  toastsSnapshot = Array.from(entries.values(), (entry) => entry.toast)
  listeners.forEach((listener) => listener())
}

export function subscribeUndoToasts(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getUndoToasts(): UndoToast[] {
  return toastsSnapshot
}

function finish(id: number, outcome: 'commit' | 'undo') {
  const entry = entries.get(id)
  if (!entry) return
  clearTimeout(entry.timer)
  entries.delete(id)
  emit()
  if (outcome === 'commit') {
    entry.commit()
  } else {
    entry.revert()
  }
}

/** Desfaz a ação do toast: reverte a UI sem nunca ter chamado o servidor. */
export function undoToast(id: number) {
  finish(id, 'undo')
}

// Em unload real, não há transporte durável para os commits atuais (Inertia
// router.post/patch). Cancela a mudança otimista em vez de fingir que o
// servidor recebeu uma ação que o navegador pode abortar.
function cancelPendingActions() {
  Array.from(entries.keys()).forEach((id) => finish(id, 'undo'))
}

export function undoableAction(options: UndoableActionOptions): void {
  const {
    message,
    apply,
    revert,
    commit,
    undoLabel = 'Desfazer',
    duration = UNDO_TOAST_DURATION_MS,
  } = options

  if (!pageHideBound && typeof window !== 'undefined') {
    window.addEventListener('pagehide', cancelPendingActions)
    pageHideBound = true
  }

  apply()

  const id = nextId++
  const timer = setTimeout(() => finish(id, 'commit'), duration)
  entries.set(id, { toast: { id, message, undoLabel }, timer, revert, commit })
  emit()
}
